import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Clock, AlertCircle, DollarSign } from "lucide-react";
import { format, isPast } from "date-fns";

interface PaymentScheduleTrackerProps {
  dealId: string;
  totalAmount: number;
}

export const PaymentScheduleTracker = ({ dealId, totalAmount }: PaymentScheduleTrackerProps) => {
  const queryClient = useQueryClient();
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [paymentDate, setPaymentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");

  const { data: payments, isLoading } = useQuery({
    queryKey: ["payment-schedule", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_payment_schedule")
        .select("*")
        .eq("deal_id", dealId)
        .order("due_date");
      
      if (error) throw error;
      return data;
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: async ({ id, payment_date, payment_method, payment_reference, notes }: {
      id: string;
      payment_date: string;
      payment_method: string;
      payment_reference: string;
      notes: string;
    }) => {
      const { error } = await supabase
        .from("deal_payment_schedule")
        .update({
          status: "paid",
          payment_date,
          payment_method,
          payment_reference,
          notes,
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-schedule", dealId] });
      toast.success("Payment marked as paid");
      setSelectedPayment(null);
      resetForm();
    },
    onError: () => {
      toast.error("Failed to update payment");
    },
  });

  const resetForm = () => {
    setPaymentDate(format(new Date(), "yyyy-MM-dd"));
    setPaymentMethod("");
    setPaymentReference("");
    setNotes("");
  };

  const getPaymentStatus = (payment: any) => {
    if (payment.status === "paid") return "paid";
    if (payment.status === "cancelled") return "cancelled";
    if (isPast(new Date(payment.due_date)) && payment.status === "pending") return "overdue";
    return "upcoming";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "overdue":
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case "cancelled":
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge variant="default">Paid</Badge>;
      case "overdue":
        return <Badge variant="destructive">Overdue</Badge>;
      case "cancelled":
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">Upcoming</Badge>;
    }
  };

  if (isLoading) {
    return <div>Loading payment schedule...</div>;
  }

  const totalPaid = payments?.filter(p => p.status === "paid").reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  const totalPending = payments?.filter(p => p.status === "pending").reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  const totalOverdue = payments?.filter(p => getPaymentStatus(p) === "overdue").reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  const progress = (totalPaid / totalAmount) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Schedule</CardTitle>
        <CardDescription>
          Track payment installments and status
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Total Paid</p>
              </div>
              <p className="text-2xl font-bold text-green-600">${totalPaid.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
              <p className="text-2xl font-bold">${totalPending.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Overdue</p>
              </div>
              <p className="text-2xl font-bold text-destructive">${totalOverdue.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Progress</p>
              <p className="text-2xl font-bold">{progress.toFixed(0)}%</p>
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment List */}
        <div className="space-y-4">
          {payments?.length === 0 ? (
            <p className="text-muted-foreground">No payment schedule set for this deal.</p>
          ) : (
            payments?.map((payment) => {
              const status = getPaymentStatus(payment);
              return (
                <div key={payment.id} className="flex items-start gap-4 p-4 border rounded-lg">
                  <div className="mt-1">{getStatusIcon(status)}</div>
                  
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">
                          Installment #{payment.installment_number} - ${Number(payment.amount).toLocaleString()}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Due: {format(new Date(payment.due_date), "MMM d, yyyy")}
                        </p>
                      </div>
                      {getStatusBadge(status)}
                    </div>

                    {payment.payment_date && (
                      <p className="text-sm">
                        <strong>Paid:</strong> {format(new Date(payment.payment_date), "MMM d, yyyy")}
                      </p>
                    )}

                    {payment.payment_method && (
                      <p className="text-sm">
                        <strong>Method:</strong> {payment.payment_method}
                      </p>
                    )}

                    {payment.payment_reference && (
                      <p className="text-sm">
                        <strong>Reference:</strong> {payment.payment_reference}
                      </p>
                    )}

                    {payment.notes && (
                      <p className="text-sm text-muted-foreground">
                        <strong>Notes:</strong> {payment.notes}
                      </p>
                    )}

                    {payment.status === "pending" && (
                      <Dialog open={selectedPayment === payment.id} onOpenChange={(open) => !open && setSelectedPayment(null)}>
                        <DialogTrigger asChild>
                          <Button size="sm" onClick={() => setSelectedPayment(payment.id)}>
                            Mark as Paid
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Mark Payment as Paid</DialogTitle>
                            <DialogDescription>
                              Record payment details for installment #{payment.installment_number}
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Payment Date *</Label>
                              <Input
                                type="date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Payment Method *</Label>
                              <Input
                                placeholder="Check, ACH, Wire, Stripe, etc."
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Payment Reference</Label>
                              <Input
                                placeholder="Check #, Transaction ID, etc."
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Notes</Label>
                              <Textarea
                                placeholder="Add any additional notes..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                              />
                            </div>
                          </div>

                          <DialogFooter>
                            <Button variant="outline" onClick={() => setSelectedPayment(null)}>
                              Cancel
                            </Button>
                            <Button
                              onClick={() => markPaidMutation.mutate({
                                id: payment.id,
                                payment_date: paymentDate,
                                payment_method: paymentMethod,
                                payment_reference: paymentReference,
                                notes,
                              })}
                              disabled={markPaidMutation.isPending || !paymentDate || !paymentMethod}
                            >
                              Mark as Paid
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};
